import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { Dna, Loader2, Sparkles, Save, ArrowLeft, TrendingUp } from "lucide-react";
import { toast } from "sonner";
import { api, apiError } from "@/lib/api";
import { PageHeader, Spinner, GoldButton, GhostButton, inputCls } from "@/components/app/widgets";

const DNA_SECTIONS = [
  {
    key: "audience", title: "Audience DNA",
    fields: [
      ["primary_audience", "Primary audience"], ["secondary_audience", "Secondary audience"],
      ["demographics", "Demographics"], ["problems", "Problems", true],
      ["pain_points", "Pain points", true], ["desires", "Desires", true],
      ["goals", "Goals", true], ["objections", "Objections", true],
    ],
  },
  {
    key: "transformation", title: "Transformation",
    fields: [
      ["before_state", "Current state (before)"], ["after_state", "Desired state (after)"],
      ["core_transformation", "Core transformation"],
    ],
  },
  {
    key: "offer", title: "Offer DNA",
    fields: [
      ["core_offer", "Core offer"], ["offer_portfolio", "Offer portfolio", true],
      ["preparation_benchmarks", "Preparation benchmarks", true],
      ["lead_magnets", "Lead magnets", true],
      ["free_value_assets", "Free value assets", true], ["bonuses", "Bonuses", true],
      ["calls_to_action", "Calls to action", true],
    ],
  },
  {
    key: "partnership", title: "Partnership DNA",
    fields: [
      ["ideal_partner_types", "Ideal partner types", true], ["ideal_platforms", "Ideal platforms", true],
      ["ideal_audience_characteristics", "Ideal audience characteristics"],
      ["preferred_collaboration_formats", "Preferred collaboration formats", true],
      ["value_first_opportunities", "Value-first opportunities", true],
    ],
  },
  {
    key: "conversion", title: "Conversion DNA",
    fields: [
      ["lead_capture_destination", "Lead capture destination"],
      ["conversion_mechanism", "Conversion mechanism"],
      ["primary_cta", "Primary CTA"], ["secondary_cta", "Secondary CTA"],
    ],
  },
];

const SRC_META = {
  verified: ["Provided / Verified", "text-emerald-400 border-emerald-500/40"],
  inference: ["AI Inference", "text-amber-400 border-amber-500/40"],
  unknown: ["Unknown", "text-zinc-500 border-white/10"],
};

function SourceBadge({ source, fieldKey }) {
  const meta = SRC_META[source];
  if (!meta) return null;
  return (
    <span data-testid={`dna-src-${fieldKey}`} className={`ml-2 inline-block rounded-full border px-1.5 py-0.5 text-[8px] font-bold uppercase tracking-wider ${meta[1]}`}>
      {meta[0]}
    </span>
  );
}

export default function ProductDetail() {
  const { productId } = useParams();
  const [product, setProduct] = useState(null);
  const [dna, setDna] = useState({});
  const [sources, setSources] = useState({});
  const [savedDna, setSavedDna] = useState({});
  const [reviewMode, setReviewMode] = useState(false);
  const [completeness, setCompleteness] = useState(null);
  const [perf, setPerf] = useState(null);
  const [busy, setBusy] = useState("");

  const load = async () => {
    try {
      const [p, list] = await Promise.all([
        api.get(`/products/${productId}`),
        api.get("/products"),
      ]);
      setProduct(p.data);
      setDna(p.data.dna || {});
      setSavedDna(p.data.dna || {});
      setSources(p.data.dna_field_sources || {});
      setReviewMode(false);
      const item = list.data.items.find((x) => x.product_id === productId);
      setCompleteness(item?.dna_completeness || null);
      api.get(`/products/${productId}/performance`).then((r) => setPerf(r.data)).catch(() => {});
    } catch (err) {
      toast.error(apiError(err));
    }
  };
  useEffect(() => { load(); }, [productId]);

  const getVal = (section, field) => dna?.[section]?.[field] ?? "";
  const getList = (section, field) => {
    const v = dna?.[section]?.[field];
    return Array.isArray(v) ? v.join("\n") : v || "";
  };
  const setVal = (section, field, value, isList) => {
    setDna((d) => ({
      ...d,
      [section]: {
        ...(d[section] || {}),
        [field]: isList ? value.split(/\n+/).map((s) => s.trim()).filter(Boolean) : value,
      },
    }));
  };

  const save = async () => {
    setBusy("save");
    try {
      const r = await api.put(`/products/${productId}/dna`, { dna, field_sources: sources });
      setCompleteness(r.data.completeness);
      setSavedDna(dna);
      setReviewMode(false);
      toast.success("Product DNA saved — all engines now use it");
    } catch (err) {
      toast.error(apiError(err));
    } finally {
      setBusy("");
    }
  };

  const aiComplete = async () => {
    setBusy("ai");
    try {
      const r = await api.post(`/products/${productId}/dna/generate`);
      setDna(r.data.dna);
      setSources(r.data.field_sources || {});
      setCompleteness(r.data.completeness);
      setReviewMode(true);
      toast.success("AI completion ready for review — nothing saved yet");
    } catch (err) {
      toast.error(apiError(err));
    } finally {
      setBusy("");
    }
  };

  const discardDraft = () => {
    setDna(savedDna);
    setReviewMode(false);
    toast.info("AI draft discarded — back to your saved DNA");
  };

  if (!product) return <Spinner label="Loading product" />;

  return (
    <div data-testid="product-dna-page">
      <PageHeader
        title={product.name}
        sub={
          <span className="flex items-center gap-3">
            <Link to="/app/products" className="flex items-center gap-1 text-[#D4AF37] hover:brightness-110">
              <ArrowLeft className="h-3.5 w-3.5" /> Products
            </Link>
            <span>·</span><span>{product.niche || product.category || "General"}</span>
          </span>
        }
      >
        <GhostButton onClick={aiComplete} disabled={!!busy} data-testid="dna-ai-complete-button">
          {busy === "ai" ? <Loader2 className="h-4 w-4 animate-spin" /> : <Sparkles className="h-4 w-4" />}
          ✨ AI COMPLETE DNA
        </GhostButton>
        <GoldButton onClick={save} disabled={!!busy} data-testid="dna-save-button">
          {busy === "save" ? <Loader2 className="h-4 w-4 animate-spin" /> : <Save className="h-4 w-4" />}
          {reviewMode ? "Save Reviewed DNA" : "Save DNA"}
        </GoldButton>
      </PageHeader>

      {reviewMode && (
        <div className="mb-8 border border-[#D4AF37]/40 bg-[#D4AF37]/5 p-5" data-testid="dna-review-banner">
          <p className="text-sm font-semibold text-[#D4AF37]">AI completion applied for review — nothing is saved yet.</p>
          <p className="mt-1 text-xs leading-relaxed text-zinc-400">
            Every field is classified: <span className="font-semibold text-emerald-400">Provided / Verified</span> = directly supported by your product data
            · <span className="font-semibold text-amber-400">AI Inference</span> = reasonable strategic inference
            · <span className="font-semibold text-zinc-500">Unknown</span> = left blank, nothing invented.
            Your authoritative offer data (four offers, pricing, access durations, preparation benchmarks) is preserved exactly.
            Edit any field, then <span className="text-zinc-200">Save Reviewed DNA</span>.
          </p>
          <button onClick={discardDraft} data-testid="dna-discard-draft" className="mt-3 border border-white/10 px-3 py-1.5 text-[10px] font-bold uppercase tracking-wider text-zinc-400 transition-colors hover:border-red-500/50 hover:text-red-400">
            Discard draft
          </button>
        </div>
      )}

      {perf && (
        <section className="mb-8 border border-[#27272A] bg-[#0A0A0A] p-6" data-testid="product-performance">
          <h2 className="flex items-center gap-2 text-xs font-bold uppercase tracking-[0.2em] text-zinc-300">
            <TrendingUp className="h-4 w-4 text-[#D4AF37]" /> Distribution Performance
            <span className="ml-2 text-[10px] normal-case tracking-normal text-zinc-600">real data · demo excluded</span>
          </h2>
          <div className="mt-4 flex flex-wrap gap-x-6 gap-y-2 text-sm text-zinc-400">
            <span><span className="font-serif text-lg font-semibold text-white">{perf.prospects}</span> prospects</span>
            <span><span className="font-serif text-lg font-semibold text-white">{perf.partnerships}</span> partnerships</span>
            <span><span className="font-serif text-lg font-semibold text-white">{perf.leads}</span> leads</span>
            <span><span className="font-serif text-lg font-semibold text-white">{perf.customers}</span> customers</span>
            <span><span className="font-serif text-lg font-semibold text-[#D4AF37]">${perf.revenue.toLocaleString()}</span> revenue</span>
            <span><span className="font-serif text-lg font-semibold text-amber-400">{perf.follow_ups_due}</span> follow-ups due</span>
          </div>
          <div className="mt-5 grid gap-4 md:grid-cols-2 xl:grid-cols-4">
            <div className="border border-white/10 p-4">
              <p className="text-[10px] font-bold uppercase tracking-wider text-zinc-500">Top partners</p>
              {perf.top_partners.length ? (
                <ul className="mt-2 space-y-1.5 text-xs">
                  {perf.top_partners.map((t) => (
                    <li key={t.prospect_id} className="flex justify-between gap-2">
                      <Link to={`/app/prospects/${t.prospect_id}`} className="truncate text-zinc-300 hover:text-[#D4AF37]">{t.name}</Link>
                      <span className="shrink-0 font-semibold text-[#D4AF37]">${t.revenue.toLocaleString()}</span>
                    </li>
                  ))}
                </ul>
              ) : <p className="mt-2 text-xs text-zinc-600">None yet</p>}
            </div>
            <div className="border border-white/10 p-4">
              <p className="text-[10px] font-bold uppercase tracking-wider text-zinc-500">Top platforms</p>
              {Object.keys(perf.top_platforms).length ? (
                <ul className="mt-2 space-y-1.5 text-xs">
                  {Object.entries(perf.top_platforms).slice(0, 5).map(([pl, s]) => (
                    <li key={pl} className="flex justify-between gap-2">
                      <span className="capitalize text-zinc-300">{pl}</span>
                      <span className="text-zinc-500">{s.leads} leads · <span className="text-[#D4AF37]">${s.revenue.toLocaleString()}</span></span>
                    </li>
                  ))}
                </ul>
              ) : <p className="mt-2 text-xs text-zinc-600">None yet</p>}
            </div>
            <div className="border border-white/10 p-4">
              <p className="text-[10px] font-bold uppercase tracking-wider text-zinc-500">Top value-first offers</p>
              {Object.keys(perf.top_value_offers).length ? (
                <ul className="mt-2 space-y-1.5 text-xs text-zinc-300">
                  {Object.entries(perf.top_value_offers).map(([offer, n]) => (
                    <li key={offer} className="truncate">· {offer} <span className="text-zinc-600">({n})</span></li>
                  ))}
                </ul>
              ) : <p className="mt-2 text-xs text-zinc-600">None yet</p>}
            </div>
            <div className="border border-white/10 p-4">
              <p className="text-[10px] font-bold uppercase tracking-wider text-zinc-500">Top campaigns</p>
              {perf.top_campaigns.length ? (
                <ul className="mt-2 space-y-1.5 text-xs">
                  {perf.top_campaigns.map((c) => (
                    <li key={c.campaign_id} className="flex justify-between gap-2">
                      <Link to={`/app/campaigns/${c.campaign_id}`} className="truncate text-zinc-300 hover:text-[#D4AF37]">{c.name}</Link>
                      <span className="shrink-0 text-zinc-500">{c.leads}L · <span className="text-[#D4AF37]">${c.revenue.toLocaleString()}</span></span>
                    </li>
                  ))}
                </ul>
              ) : <p className="mt-2 text-xs text-zinc-600">None yet</p>}
            </div>
          </div>
        </section>
      )}

      <div className="mb-8 border border-[#27272A] bg-[#0A0A0A] p-5">
        <div className="flex items-center justify-between text-xs">
          <span className="flex items-center gap-2 font-bold uppercase tracking-[0.18em] text-zinc-400">
            <Dna className="h-4 w-4 text-[#D4AF37]" /> Product DNA Completeness
          </span>
          <span className={`font-bold ${completeness?.complete ? "text-emerald-400" : "text-amber-400"}`} data-testid="dna-completeness">
            {completeness ? `${completeness.percent}% — ${completeness.filled}/${completeness.total} fields${completeness.complete ? "" : " (incomplete)"}` : "—"}
          </span>
        </div>
        <div className="mt-3 h-1.5 overflow-hidden rounded-full bg-white/10">
          <div className="h-full rounded-full bg-[#D4AF37] transition-[width] duration-500" style={{ width: `${completeness?.percent || 0}%` }} />
        </div>
        <p className="mt-2 text-[11px] text-zinc-600">
          Nothing is mandatory — fill what genuinely applies. Complete DNA powers sharper partner matching and campaign strategy.
        </p>
      </div>

      <div className="space-y-6">
        {DNA_SECTIONS.map((section) => (
          <section key={section.key} data-testid={`dna-section-${section.key}`} className="border border-[#27272A] bg-[#0A0A0A] p-6">
            <h2 className="text-xs font-bold uppercase tracking-[0.2em] text-[#D4AF37]">{section.title}</h2>
            <div className="mt-5 grid gap-4 sm:grid-cols-2">
              {section.fields.map(([field, label, isList]) => (
                <label key={field} className="block">
                  <span className="mb-1.5 block text-[11px] font-semibold uppercase tracking-[0.14em] text-zinc-500">
                    {label}{isList ? " (one per line)" : ""}
                    <SourceBadge source={sources[`${section.key}.${field}`]} fieldKey={`${section.key}-${field}`} />
                  </span>
                  {isList || ["demographics", "before_state", "after_state", "core_transformation", "core_offer", "ideal_audience_characteristics"].includes(field) ? (
                    <textarea
                      rows={2}
                      value={isList ? getList(section.key, field) : getVal(section.key, field)}
                      onChange={(e) => setVal(section.key, field, e.target.value, !!isList)}
                      data-testid={`dna-${section.key}-${field}`}
                      className={inputCls}
                    />
                  ) : (
                    <input
                      type="text"
                      value={getVal(section.key, field)}
                      onChange={(e) => setVal(section.key, field, e.target.value, false)}
                      data-testid={`dna-${section.key}-${field}`}
                      className={inputCls}
                    />
                  )}
                </label>
              ))}
            </div>
          </section>
        ))}
      </div>
    </div>
  );
}
